import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Eye, Fuel, Gauge, PackagePlus, Send, TriangleAlert, Wallet, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { DOCUMENT_TYPE_BG, countryName } from '@/i18n';
import { formatDateTime, formatKm, formatMinor, formatNumber, formatWhen } from '@/lib/format';
import { api, ApiError } from '@/api/client';
import { qk } from '@/api/keys';
import { useAttachOrder, useDetachOrder, useTripSuggestions } from '@/api/hooks/trips';
import type { TripSheet } from '@/api/types';
import { Badge, StatusPill } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, Stat } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Field';
import { LoadBar } from '@/components/dispatch/LoadBar';
import { SingleOrderReasonField } from '@/components/dispatch/SingleOrderReasonField';
import { DocSetChips, UploadButton } from '@/components/documents/DocSet';
import { OrderDocActions } from '@/components/documents/OrderDocActions';
import { toast } from '@/stores/toast';

const EXPENSE_BG: Record<string, string> = {
  fuel: 'Гориво',
  adblue: 'AdBlue',
  toll: 'Тол такса',
  vignette: 'Винетка',
  ferry: 'Ферибот',
  parking: 'Паркинг',
  accommodation: 'Нощувка',
  per_diem: 'Дневни',
  repair: 'Ремонт',
  tyres: 'Гуми',
  wash: 'Измиване',
  fine: 'Глоба',
  customs_fee: 'Такса',
  subcontractor: 'Подизпълнител',
  other: 'Друго',
};

const PLANNABLE = ['draft', 'planned', 'dispatched', 'accepted'];

/**
 * ─────────────────────────────────────────────────────────────────────────
 *  КУРСЪТ НА ЕДИН ЛИСТ — „цъкаш върху буса и виждаш всичко за курса".
 *
 *  Отгоре колко е пълен бусът (тегло и обем), после заявките с трите им
 *  документа (заявка, ЧМР, фактура), горивото, заплатата на шофьора на
 *  километър и всички файлове. Ако бусът е с една заявка и не е пълен, листът
 *  предлага чакащи заявки, които се побират и са по пътя.
 * ─────────────────────────────────────────────────────────────────────────
 */
export function TripSheetView({
  sheet,
  compact = false,
  docActions = false,
  canIssue = false,
}: {
  sheet: TripSheet;
  compact?: boolean;
  /** Под всяка заявка — трите документа с „Отвори", „Приеми", „Върни", „Качи". */
  docActions?: boolean;
  /** Може ли да издава фактура (управител, счетоводител). */
  canIssue?: boolean;
}) {
  const { trip, load } = sheet;
  const plannable = PLANNABLE.includes(trip.status);
  const detach = useDetachOrder(trip.id);
  /** Заявка с пълен комплект е сгъната; непълната е отворена — там има работа. */
  const [toggled, setToggled] = useState<Set<string>>(new Set());
  const docsOpen = (orderId: string, complete: boolean) => (complete ? toggled.has(orderId) : !toggled.has(orderId));
  const toggleDocs = (orderId: string) =>
    setToggled((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });

  return (
    <div className="space-y-3 sm:space-y-4">
      <Card>
        <CardHeader
          title={
            <span className="flex flex-wrap items-center gap-2">
              <Link to="/trips/$tripId" params={{ tripId: trip.id }} className="font-mono hover:text-accent">
                {trip.number}
              </Link>
              <StatusPill status={trip.status} kind="trip" />
              {load.full ? <Badge tone="accent">пълен</Badge> : null}
              {load.overweight ? <Badge tone="danger">претоварен</Badge> : null}
            </span>
          }
          subtitle={[
            trip.driverName ?? 'без шофьор',
            trip.startedAt ? `тръгнал ${formatWhen(trip.startedAt)}` : trip.plannedStartAt ? `тръгва ${formatWhen(trip.plannedStartAt)}` : null,
            trip.completedAt ? `приключил ${formatWhen(trip.completedAt)}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
          actions={<DispatchButton sheet={sheet} />}
        />
        <CardBody className="grid gap-3 sm:grid-cols-2">
          <LoadBar label="Тегло" value={load.weightKg} max={load.payloadKg} unit="кг" />
          <LoadBar label="Обем" value={load.volumeM3} max={load.cargoVolumeM3} unit="m³" digits={1} />
          {load.missingWeight > 0 || load.missingVolume > 0 ? (
            <p className="text-[11px] text-warn sm:col-span-2">
              {load.missingWeight > 0 ? `${load.missingWeight} заявки без тегло. ` : ''}
              {load.missingVolume > 0 ? `${load.missingVolume} заявки без обем. ` : ''}
              Сметката колко е пълен бусът е непълна.
            </p>
          ) : null}
          {sheet.singleOrder.reason ? (
            <p className="text-xs text-ink-dim sm:col-span-2">
              Тръгна с една заявка: <span className="text-ink">{sheet.singleOrder.reason}</span>
            </p>
          ) : sheet.singleOrder.applies && plannable ? (
            <p className="flex items-start gap-1.5 text-xs text-warn sm:col-span-2">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              Курсът е с една заявка, а бусът не е пълен. Добавете заявки по пътя — предложенията са по-долу.
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader
          title={`Заявки в курса (${sheet.orders.length})`}
          subtitle="Всяка заявка събира три документа: заявка, ЧМР и фактура."
        />
        {sheet.orders.length === 0 ? (
          <CardBody>
            <p className="text-xs text-ink-dim">Курсът още няма заявки.</p>
          </CardBody>
        ) : (
          <ul className="divide-y divide-line">
            {sheet.orders.map((order) => (
              <li key={order.orderId} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2.5 text-sm">
                <Link
                  to="/orders/$orderId"
                  params={{ orderId: order.orderId }}
                  className="font-mono text-accent hover:underline"
                >
                  {order.number}
                </Link>
                <span className="min-w-0 truncate text-ink-dim">{order.partnerName ?? '—'}</span>
                <span className="w-full text-xs text-ink-dim sm:w-auto">
                  {order.from ?? '—'} → {order.to ?? '—'}
                </span>
                <span className="font-mono text-xs tabular text-ink-dim">
                  {order.weightKg !== null ? `${formatNumber(order.weightKg)} кг` : 'без тегло'}
                  {' · '}
                  {order.volumeM3 !== null ? `${order.volumeM3.toLocaleString('bg-BG')} m³` : 'без обем'}
                </span>
                <span className="ml-auto flex items-center gap-2">
                  {docActions ? (
                    <button
                      type="button"
                      onClick={() => toggleDocs(order.orderId)}
                      aria-expanded={docsOpen(order.orderId, order.docs.complete)}
                      className="inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-raised"
                      title="Документите на заявката"
                    >
                      <DocSetChips docs={order.docs} />
                      <ChevronDown
                        className={cn('size-3.5 text-ink-faint transition-transform', docsOpen(order.orderId, order.docs.complete) && 'rotate-180')}
                      />
                    </button>
                  ) : (
                    <DocSetChips docs={order.docs} />
                  )}
                  {plannable && sheet.orders.length > 1 ? (
                    <button
                      type="button"
                      title="Свали заявката от курса"
                      className="text-ink-faint hover:text-danger"
                      onClick={() =>
                        detach.mutate(order.orderId, { onSuccess: () => toast.ok(`${order.number} е свалена от курса`) })
                      }
                    >
                      <X className="size-4" />
                    </button>
                  ) : null}
                </span>
                {docActions && docsOpen(order.orderId, order.docs.complete) ? (
                  <OrderDocActions orderId={order.orderId} docs={order.docs} canIssue={canIssue} />
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {plannable && !load.full ? <Suggestions tripId={trip.id} /> : null}
      </Card>

      <div className={cn('grid gap-3 sm:gap-4', compact ? 'lg:grid-cols-2' : 'lg:grid-cols-3')}>
        <PayCard sheet={sheet} />
        <FuelCard sheet={sheet} />
        {!compact ? <ExpensesCard sheet={sheet} /> : null}
      </div>

      <DocumentsCard sheet={sheet} />
    </div>
  );
}

/** „Изпрати към шофьора" — с причина, ако бусът тръгва с една заявка и не е пълен. */
function DispatchButton({ sheet }: { sheet: TripSheet }) {
  const { trip } = sheet;
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const send = useMutation({
    meta: { silent: true },
    mutationFn: async (input: { singleOrderReason?: string }) => {
      if (trip.status === 'draft') await api.post(`/trips/${trip.id}/status`, { to: 'planned' });
      return api.post(`/trips/${trip.id}/status`, { to: 'dispatched', ...input });
    },
    onSuccess: () => {
      toast.ok('Курсът е изпратен към шофьора', trip.number);
      setAsking(false);
      void queryClient.invalidateQueries({ queryKey: qk.trips.all });
      void queryClient.invalidateQueries({ queryKey: qk.board.all });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'SINGLE_ORDER_REASON_REQUIRED') {
        setAsking(true);
        return;
      }
      toast.error(error instanceof ApiError ? error.message : 'Курсът не беше изпратен.');
    },
  });
  if (trip.status !== 'draft' && trip.status !== 'planned') return null;
  if (!trip.vehicleId || !trip.driverId) return null;
  return (
    <div className="flex flex-col items-end gap-2">
      <Button
        size="sm"
        variant="primary"
        icon={<Send className="size-3.5" />}
        loading={send.isPending}
        disabled={asking && reason.trim().length < 3}
        onClick={() => send.mutate(asking ? { singleOrderReason: reason.trim() } : {})}
      >
        Изпрати към шофьора
      </Button>
      {asking ? (
        <div className="w-80 max-w-full">
          <SingleOrderReasonField
            value={reason}
            onChange={setReason}
            loadNote={[
              sheet.load.weightPct !== null ? `${sheet.load.weightPct} % тегло` : null,
              sheet.load.volumePct !== null ? `${sheet.load.volumePct} % обем` : null,
            ]
              .filter(Boolean)
              .join(', ')}
          />
        </div>
      ) : null}
    </div>
  );
}

/** Чакащи заявки, които се побират и са по пътя — „Добави" ги качва в курса. */
function Suggestions({ tripId }: { tripId: string }) {
  const { data } = useTripSuggestions(tripId);
  const attach = useAttachOrder(tripId);
  const [allowVolume, setAllowVolume] = useState(false);
  if (!data || data.length === 0) return null;
  return (
    <div className="border-t border-line bg-raised/40 px-4 py-3">
      <div className="mb-2 flex items-center gap-2 text-xs font-medium text-ink">
        <PackagePlus className="size-3.5 text-accent" />
        Побират се в буса
        <Checkbox
          className="ml-auto text-[11px] font-normal"
          label="разреши обем над капацитета"
          checked={allowVolume}
          onChange={(e) => setAllowVolume(e.target.checked)}
        />
      </div>
      <ul className="space-y-1.5">
        {data.map((s) => (
          <li key={s.orderId} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <Link to="/orders/$orderId" params={{ orderId: s.orderId }} className="font-mono text-accent hover:underline">
              {s.number}
            </Link>
            <span className="text-ink-dim">
              {s.from ?? '—'} → {s.to ?? '—'}
            </span>
            <span className="font-mono text-ink-dim">
              {s.weightKg !== null ? `${formatNumber(s.weightKg)} кг` : '—'} ·{' '}
              {s.volumeM3 !== null ? `${s.volumeM3.toLocaleString('bg-BG')} m³` : '—'}
            </span>
            {s.pickupKmFromRoute !== null ? (
              <Badge tone={s.pickupKmFromRoute <= 50 ? 'ok' : 'neutral'}>
                товарене на {formatKm(s.pickupKmFromRoute)} от курса
              </Badge>
            ) : null}
            {s.sameDirection ? <Badge tone="info">същата посока</Badge> : null}
            <Button
              size="sm"
              variant="secondary"
              className="ml-auto"
              loading={attach.isPending && attach.variables?.orderId === s.orderId}
              onClick={() =>
                attach.mutate(
                  { orderId: s.orderId, ...(allowVolume ? { allowVolumeOverflow: true } : {}) },
                  { onSuccess: () => toast.ok(`${s.number} е качена в курса`) },
                )
              }
            >
              Добави
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PayCard({ sheet }: { sheet: TripSheet }) {
  const { pay } = sheet;
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            <Wallet className="size-4 text-ink-dim" />
            Заплата на шофьора
          </span>
        }
        subtitle={pay.driverName ?? 'без шофьор'}
      />
      <CardBody className="grid grid-cols-2 gap-3">
        <Stat
          label="Километри"
          value={pay.usedKm !== null ? formatKm(pay.usedKm) : '—'}
          hint={pay.source === 'odometer' ? 'по километража' : pay.source === 'gps' ? 'по GPS' : 'няма отчет'}
        />
        {pay.visible ? (
          <Stat
            label="За курса"
            value={pay.payMinor !== null ? formatMinor(pay.payMinor, pay.ccy) : '—'}
            hint={
              pay.ratePerKmMinorX100 !== null
                ? `${(pay.ratePerKmMinorX100 / 10000).toLocaleString('bg-BG', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} €/км`
                : 'няма ставка на км'
            }
            tone="accent"
          />
        ) : (
          <Stat label="За курса" value="—" hint="вижда се от управителя" />
        )}
        <div className="col-span-2 space-y-1 text-xs text-ink-dim">
          <div className="flex items-center gap-1.5">
            <Gauge className="size-3.5" />
            Километраж: {pay.odometerStartKm !== null ? formatNumber(pay.odometerStartKm) : '—'} →{' '}
            {pay.odometerEndKm !== null ? formatNumber(pay.odometerEndKm) : '—'}
          </div>
          <div>GPS: {pay.gpsKm !== null ? formatKm(pay.gpsKm) : 'няма данни'}</div>
          {pay.deviationPct !== null ? (
            <div className={pay.anomaly ? 'font-medium text-danger' : undefined}>
              Разлика километраж/GPS: {pay.deviationPct.toLocaleString('bg-BG')} %
              {pay.anomaly ? ' — над 10 %, проверете' : ''}
            </div>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}

function FuelCard({ sheet }: { sheet: TripSheet }) {
  const { fuel } = sheet;
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            <Fuel className="size-4 text-ink-dim" />
            Гориво на буса
          </span>
        }
        subtitle={`${fuel.transactions.length} зареждания`}
      />
      <CardBody className="space-y-2">
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Литри" value={fuel.litres > 0 ? fuel.litres.toLocaleString('bg-BG', { maximumFractionDigits: 1 }) : '—'} />
          <Stat
            label="л/100 км"
            value={fuel.litresPer100km !== null ? fuel.litresPer100km.toLocaleString('bg-BG') : '—'}
          />
          <Stat
            label="Сума"
            className="col-span-2"
            value={fuel.totals.length ? fuel.totals.map((x) => formatMinor(x.minor, x.ccy)).join(' + ') : '—'}
          />
        </div>
        {fuel.transactions.length > 0 ? (
          <ul className="max-h-48 space-y-1 overflow-y-auto text-xs">
            {fuel.transactions.map((f) => (
              <li key={f.id} className="flex flex-wrap gap-x-2">
                <span className="font-mono text-ink-faint">{formatDateTime(f.at)}</span>
                <span className="text-ink-dim">
                  {f.country ? countryName(f.country) : ''} {f.station ?? ''}
                </span>
                <span className="ml-auto font-mono">
                  {f.litres !== null ? `${f.litres.toLocaleString('bg-BG')} л · ` : ''}
                  {formatMinor(f.grossMinor, f.ccy)}
                </span>
                {f.anomalies.length > 0 ? <Badge tone="warn">{f.anomalies.length} аномалии</Badge> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-ink-faint">Няма зареждания по този курс.</p>
        )}
      </CardBody>
    </Card>
  );
}

function ExpensesCard({ sheet }: { sheet: TripSheet }) {
  return (
    <Card>
      <CardHeader title="Други разходи" subtitle={`${sheet.expenses.length} записа`} />
      <CardBody>
        {sheet.expenses.length === 0 ? (
          <p className="text-xs text-ink-faint">Няма разходи по курса.</p>
        ) : (
          <ul className="max-h-56 space-y-1 overflow-y-auto text-xs">
            {sheet.expenses.map((e) => (
              <li key={e.id} className="flex flex-wrap gap-x-2">
                <span className="font-medium">{EXPENSE_BG[e.kind] ?? e.kind}</span>
                <span className="text-ink-dim">{e.description ?? ''}</span>
                <span className="ml-auto font-mono">{formatMinor(e.amountMinor, e.ccy)}</span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function DocumentsCard({ sheet }: { sheet: TripSheet }) {
  return (
    <Card>
      <CardHeader
        title="Документи по курса"
        subtitle={`${sheet.documents.length} файла`}
        actions={<UploadButton type="other" target={{ tripId: sheet.trip.id }} label="Качи файл" />}
      />
      <CardBody>
        {sheet.documents.length === 0 ? (
          <p className="text-xs text-ink-faint">Още няма документи.</p>
        ) : (
          <ul className="divide-y divide-line">
            {sheet.documents.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
                <span className="font-medium">{DOCUMENT_TYPE_BG[d.type] ?? d.type}</span>
                {d.orderNumber ? <span className="font-mono text-xs text-ink-dim">{d.orderNumber}</span> : null}
                <span className="min-w-0 truncate text-xs text-ink-faint">{d.fileName}</span>
                <Badge tone={d.status === 'accepted' ? 'ok' : d.status === 'rejected' ? 'danger' : 'warn'}>
                  {d.status === 'accepted'
                    ? 'приет'
                    : d.status === 'rejected'
                      ? d.reshootRequestedAt
                        ? 'върнат · чака нова снимка'
                        : 'върнат'
                      : 'за преглед'}
                </Badge>
                {d.driverConfirmedAt ? (
                  <span className="text-[11px] text-ok" title="Шофьорът е потвърдил на телефона, че снимката се чете">
                    ✓ шофьор
                  </span>
                ) : null}
                {d.previewUrl ? (
                  <a
                    href={d.previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto inline-flex items-center gap-1 text-xs text-accent hover:underline"
                  >
                    <Eye className="size-3.5" />
                    отвори
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
